(() => {
  const instances = new Map();

  class CtrMeasureResults {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId || "";
      this.tabs = Array.from(section.querySelectorAll("[data-ctr-measure-tab]"));
      this.panels = Array.from(section.querySelectorAll("[data-ctr-measure-panel]"));
      this.slides = Array.from(section.querySelectorAll("[data-ctr-measure-slide]"));
      this.dots = Array.from(section.querySelectorAll("[data-ctr-measure-dot]"));
      this.prevButton = section.querySelector("[data-ctr-measure-prev]");
      this.nextButton = section.querySelector("[data-ctr-measure-next]");
      this.controls = section.querySelector("[data-ctr-measure-controls]");
      this.activeIndex = 0;

      this.onTabClick = this.onTabClick.bind(this);
      this.onPrevClick = this.onPrevClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.bind();
      this.show(0);
    }

    bind() {
      this.tabs.forEach((tab) => tab.addEventListener("click", this.onTabClick));
      this.dots.forEach((dot) => {
        dot.addEventListener("click", () => {
          const index = Number(dot.dataset.index);
          if (!Number.isNaN(index)) this.show(index);
        });
      });
      if (this.prevButton) this.prevButton.addEventListener("click", this.onPrevClick);
      if (this.nextButton) this.nextButton.addEventListener("click", this.onNextClick);
      document.addEventListener("shopify:block:select", this.onBlockSelect);
    }

    onTabClick(event) {
      const blockId = event.currentTarget.dataset.blockId;
      const index = this.findIndex(blockId);
      if (index > -1) this.show(index);
    }

    onPrevClick() {
      const total = this.getTotal();
      if (!total) return;
      this.show(this.activeIndex - 1);
    }

    onNextClick() {
      const total = this.getTotal();
      if (!total) return;
      this.show(this.activeIndex + 1);
    }

    onBlockSelect(event) {
      const detail = event.detail || {};
      if (detail.sectionId !== this.sectionId) return;
      const index = this.findIndex(detail.blockId);
      if (index > -1) this.show(index);
    }

    getTotal() {
      return Math.max(this.panels.length, this.slides.length, this.tabs.length);
    }

    findIndex(blockId) {
      if (!blockId) return -1;
      const panelIndex = this.panels.findIndex((item) => item.dataset.blockId === blockId);
      if (panelIndex > -1) return panelIndex;
      const slideIndex = this.slides.findIndex((item) => item.dataset.blockId === blockId);
      if (slideIndex > -1) return slideIndex;
      return this.tabs.findIndex((item) => item.dataset.blockId === blockId);
    }

    show(index) {
      const total = this.getTotal();
      if (!total) return;
      const boundedIndex = Math.max(0, Math.min(index, total - 1));
      this.activeIndex = boundedIndex;

      this.tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === this.activeIndex;
        tab.classList.toggle("is-active", isActive);
        tab.setAttribute("aria-selected", isActive ? "true" : "false");
      });

      this.panels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === this.activeIndex;
        panel.classList.toggle("is-active", isActive);
        panel.hidden = !isActive;
      });

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === this.activeIndex;
        slide.classList.toggle("is-active", isActive);
        slide.hidden = !isActive;
      });

      if (this.controls && this.slides[this.activeIndex]) {
        this.slides[this.activeIndex].appendChild(this.controls);
      }

      this.dots.forEach((dot, dotIndex) => {
        dot.classList.toggle("is-active", dotIndex === this.activeIndex);
      });

      this.updateArrowState(total);
    }

    updateArrowState(total) {
      const hasMultipleSlides = total > 1;
      const atStart = this.activeIndex === 0;
      const atEnd = this.activeIndex === total - 1;

      if (this.prevButton) {
        const disablePrev = !hasMultipleSlides || atStart;
        this.prevButton.disabled = disablePrev;
        this.prevButton.setAttribute("aria-disabled", disablePrev ? "true" : "false");
        this.prevButton.classList.toggle("is-disabled", disablePrev);
      }

      if (this.nextButton) {
        const disableNext = !hasMultipleSlides || atEnd;
        this.nextButton.disabled = disableNext;
        this.nextButton.setAttribute("aria-disabled", disableNext ? "true" : "false");
        this.nextButton.classList.toggle("is-disabled", disableNext);
      }
    }

    destroy() {
      this.tabs.forEach((tab) => tab.removeEventListener("click", this.onTabClick));
      if (this.prevButton) this.prevButton.removeEventListener("click", this.onPrevClick);
      if (this.nextButton) this.nextButton.removeEventListener("click", this.onNextClick);
      document.removeEventListener("shopify:block:select", this.onBlockSelect);
    }
  }

  const init = (root) => {
    const section = root?.querySelector?.("[data-ctr-measure-results]") || root;
    if (!section || !section.matches("[data-ctr-measure-results]")) return;
    if (instances.has(section)) return;
    instances.set(section, new CtrMeasureResults(section));
  };

  const unload = (root) => {
    const section = root?.querySelector?.("[data-ctr-measure-results]") || root;
    if (!section || !section.matches("[data-ctr-measure-results]")) return;
    const instance = instances.get(section);
    if (!instance) return;
    instance.destroy();
    instances.delete(section);
  };

  const initAll = () => {
    document.querySelectorAll("[data-ctr-measure-results]").forEach((section) => init(section));
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => init(event.target));
  document.addEventListener("shopify:section:unload", (event) => unload(event.target));
})();
